import React, { useState, useEffect } from 'react';
import './BlockDetails.css';

const BlockDetails = ({ blockHash, onClose }) => {
  const [blockData, setBlockData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState(null);

  // Fetch detailed block information
  const fetchBlockDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get ALL blocks to find this one
      const response = await fetch('http://localhost:9545', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'avo_getRecentBlocks',
          params: [10000], // Get up to 10,000 blocks to find any block
          id: 1
        })
      });

      const result = await response.json();
      if (result.result && result.result.blocks) {
        const block = result.result.blocks.find(b => b.hash === blockHash);
        
        if (block) {
          setBlockData(block);
          // Fetch transactions for this block
          await fetchBlockTransactions(block.number);
        } else {
          setError('Block not found');
        }
      } else {
        setError('Failed to fetch block data');
      }
    } catch (err) {
      console.error('Error fetching block details:', err);
      setError('Failed to load block details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions for a specific block
  const fetchBlockTransactions = async (blockNumber) => {
    try {
      setLoadingTransactions(true);
      
      const response = await fetch('http://localhost:9545', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'avo_getBlockTransactions',
          params: [blockNumber],
          id: 1
        })
      });

      const result = await response.json();
      if (result.result && result.result.transactions) {
        setTransactions(result.result.transactions);
      } else {
        console.error('Failed to fetch transactions:', result.error || 'Unknown error');
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching block transactions:', err);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    if (blockHash) {
      fetchBlockDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockHash]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text);
    });
  };

  const calculateGasUtilization = () => {
    if (!blockData) return 0;
    const gasUsed = parseInt(blockData.gasUsed);
    const gasLimit = parseInt(blockData.gasLimit);
    return ((gasUsed / gasLimit) * 100).toFixed(2);
  };

  // Convert byte array to hex address
  const formatAddress = (addressArray) => {
    if (!addressArray || !Array.isArray(addressArray)) return 'Unknown';
    return '0x' + addressArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
  };

  // Format AVO value from wei to AVO
  const formatAvoValue = (valueWei) => {
    if (!valueWei) return '0';
    const avo = parseFloat(valueWei) / 1e18;
    return avo.toFixed(6) + ' AVO';
  };

  // Truncate address for display
  const truncateAddress = (address) => {
    if (!address) return '';
    return address.slice(0, 6) + '...' + address.slice(-4);
  };

  if (loading) {
    return (
      <div className="block-details-overlay">
        <div className="block-details-modal">
          <div className="block-details-header">
            <h2>Block Details</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="loading-content">
            <p>Loading block details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="block-details-overlay">
        <div className="block-details-modal">
          <div className="block-details-header">
            <h2>Block Details</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="error-content">
            <p>Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="block-details-overlay">
      <div className="block-details-modal">
        <div className="block-details-header">
          <h2>Block Details</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="block-details-content">
          <div className="detail-section">
            <h3>Basic Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Block Number:</span>
                <span className="detail-value block-number">
                  #{blockData.number}
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Block Hash:</span>
                <span className="detail-value hash-value">
                  {blockData.hash}
                  <button 
                    className="copy-button" 
                    onClick={() => copyToClipboard(blockData.hash)}
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Timestamp:</span>
                <span className="detail-value">{blockData.timeFormatted}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Size:</span>
                <span className="detail-value">{blockData.size.toLocaleString()} bytes</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Validator Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Validator:</span>
                <span className="detail-value validator-name">
                  {blockData.validator}
                  <span className="validator-badge">VRF Selected</span>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Shard ID:</span>
                <span className="detail-value shard-value">
                  Shard {blockData.shardId}
                </span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Transaction Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Transaction Count:</span>
                <span className="detail-value tx-count">
                  {blockData.transactionCount} transaction{blockData.transactionCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Gas Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Gas Used:</span>
                <span className="detail-value gas-used">
                  {parseInt(blockData.gasUsed).toLocaleString()}
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Gas Limit:</span>
                <span className="detail-value gas-limit">
                  {parseInt(blockData.gasLimit).toLocaleString()}
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Gas Utilization:</span>
                <span className="detail-value gas-utilization">
                  {calculateGasUtilization()}%
                </span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Flow Consensus Information</h3>
            <div className="consensus-info">
              <div className="consensus-item">
                <span className="consensus-label">Consensus Algorithm:</span>
                <span className="consensus-value">Flow Consensus</span>
              </div>
              <div className="consensus-item">
                <span className="consensus-label">Validator Selection:</span>
                <span className="consensus-value">VRF (Verifiable Random Function)</span>
              </div>
              <div className="consensus-item">
                <span className="consensus-label">Finality:</span>
                <span className="consensus-value">Byzantine Fault Tolerant</span>
              </div>
              <div className="consensus-item">
                <span className="consensus-label">Shard Architecture:</span>
                <span className="consensus-value">Multi-Shard with Cross-Shard Support</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Technical Specifications</h3>
            <div className="tech-specs">
              <div className="spec-item">
                <span className="spec-label">Block Size Limit:</span>
                <span className="spec-value">1 MB</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Block Time:</span>
                <span className="spec-value">~30 seconds (Flow Consensus)</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Network:</span>
                <span className="spec-value">AVO Protocol Mainnet</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Transactions ({blockData.transactionCount})</h3>
            {loadingTransactions ? (
              <div className="transactions-loading">
                <p>Loading transactions...</p>
              </div>
            ) : transactions.length > 0 ? (
              <div className="transactions-list">
                {transactions.map((tx, index) => (
                  <div key={index} className="transaction-item">
                    <div className="transaction-header">
                      <span className="transaction-index">#{index + 1}</span>
                      <span className="transaction-hash">
                        {tx.hash}
                        <button 
                          className="copy-button" 
                          onClick={() => copyToClipboard(tx.hash)}
                          title="Copy transaction hash"
                        >
                          📋
                        </button>
                      </span>
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-row">
                        <span className="transaction-label">From:</span>
                        <span className="transaction-address" title={formatAddress(tx.from)}>
                          {truncateAddress(formatAddress(tx.from))}
                          <button 
                            className="copy-button" 
                            onClick={() => copyToClipboard(formatAddress(tx.from))}
                            title="Copy from address"
                          >
                            📋
                          </button>
                        </span>
                      </div>
                      <div className="transaction-row">
                        <span className="transaction-label">To:</span>
                        <span className="transaction-address" title={formatAddress(tx.to)}>
                          {truncateAddress(formatAddress(tx.to))}
                          <button 
                            className="copy-button" 
                            onClick={() => copyToClipboard(formatAddress(tx.to))}
                            title="Copy to address"
                          >
                            📋
                          </button>
                        </span>
                      </div>
                      <div className="transaction-row">
                        <span className="transaction-label">Value:</span>
                        <span className="transaction-value">{formatAvoValue(tx.value)}</span>
                      </div>
                      <div className="transaction-row">
                        <span className="transaction-label">Gas Used:</span>
                        <span className="transaction-gas">{tx.gasUsed?.toLocaleString() || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-transactions">
                <p>No transactions found in this block.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockDetails;